import { JEWEL_CONFIG } from "./config.js";
import { buildQuery, getTradeData } from "./tradeService.js";

const DELAY = 10000;

function toChaos(item, rate) {
  const p = item?.listing?.price;
  if (!p || typeof p.amount !== "number") return null;
  return p.currency === "divine" ? p.amount * rate : p.amount;
}

function medianOfSorted(arr) {
  const n = arr.length;
  const mid = Math.floor(n / 2);
  return n % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}

function bandedMean(items, rate) {
  const amounts = items
    .map((it) => toChaos(it, rate))
    .filter((v) => typeof v === "number" && isFinite(v))
    .sort((a, b) => a - b);
  const n = amounts.length;
  if (!n) return 0;

  // Use a band around the median to drop obvious spikes
  const med = medianOfSorted(amounts);
  const lower = med * 0.5; // keep values within 0.5x .. 2x median
  const upper = med * 2.0;
  const filtered = amounts.filter((v) => v >= lower && v <= upper);

  const arr = filtered.length >= 3 ? filtered : amounts;
  const sum = arr.reduce((s, v) => s + v, 0);
  return sum / arr.length;
}

export function getCombinations(arr, k) {
  const results = [];
  const helper = (start, combo) => {
    if (combo.length === k) return results.push(combo);
    for (let i = start; i < arr.length; i++) helper(i + 1, [...combo, arr[i]]);
  };
  helper(0, []);
  return results;
}

function getValidCombos(type, sizeKey) {
  const conf = JEWEL_CONFIG[sizeKey];
  const allCombos = getCombinations(type.notables, conf.count);
  const results = [];

  for (const combo of allCombos) {
    const prefixes = combo.filter((n) => n.affix === "prefix").length;
    const suffixes = combo.filter((n) => n.affix === "suffix").length;

    for (const rule of conf.rules) {
      if (prefixes === rule.prefix && suffixes === rule.suffix) {
        results.push({ combo, rule });
        break;
      }
    }
  }

  return results;
}

export async function runAnalysis({
  size,
  typeKey,
  league,
  exchangeRate,
  sessionId,
  onProgress,
  onFetching,
  onWait, // Added callback for cooldown UI
  shouldAbort,
}) {
  const type = typeKey;
  const conf = JEWEL_CONFIG[size];
  const validCombos = getValidCombos(type, size);
  const results = [];

  for (let i = 0; i < validCombos.length; i++) {
    if (shouldAbort?.()) break;

    const { combo } = validCombos[i];
    const labels = combo.map((n) => n.label);

    if (onFetching) onFetching(labels);

    const query = buildQuery({
      size: conf.size,
      ilvl: conf.ilvl,
      enchantId: type.enchantStatId,
      option: type.enchantOption,
      notableIds: combo.map((n) => n.id),
    });

    const { total, items, id } = await getTradeData(query, league, sessionId);

    const tradeUrl = id
      ? `https://www.pathofexile.com/trade/search/${league}/${id}`
      : null;

    const avg = bandedMean(items, exchangeRate);

    const currentResult = { combo: labels, total, average: avg, url: tradeUrl };
    results.push(currentResult);

    if (onProgress) {
      onProgress(i + 1, validCombos.length, labels, currentResult);
    }

    if (i < validCombos.length - 1) {
      if (onWait) onWait(DELAY / 1000);
      await new Promise((r) => setTimeout(r, DELAY));
    }
  }

  return results;
}
